import { FileText, Clock, MapPin, CreditCard, Shield } from 'lucide-react';
import { Link } from 'react-router-dom';

export function NewPatients() {
  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <div className="text-center mb-16">
        <h1 className="text-4xl font-bold text-gray-900 mb-4">Welcome New Patients</h1>
        <p className="text-xl text-gray-600 max-w-3xl mx-auto">
          We're committed to making your first visit as smooth and comfortable as possible. 
          Here's everything you need to know to get started.
        </p>
      </div>

      <div className="grid md:grid-cols-2 gap-12 mb-16">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 mb-4">What to Expect</h2>
          <p className="text-gray-600 mb-6">
            Your first visit will include a detailed discussion of your medical history and symptoms, 
            a comprehensive neurological examination, and time for you to ask questions. Our goal is 
            to understand your needs fully and begin crafting a personalized care plan.
          </p>
          <p className="text-gray-600">
            Initial consultations typically last 60-90 minutes to ensure we have adequate time 
            for a thorough evaluation. We recommend arriving 15 minutes early to complete any 
            remaining paperwork.
          </p>
        </div>
        <div className="bg-blue-50 rounded-lg p-8">
          <h3 className="text-xl font-semibold text-gray-900 mb-4">What to Bring</h3>
          <ul className="space-y-4">
            <li className="flex items-start">
              <FileText className="h-6 w-6 text-blue-600 mr-3 mt-1" />
              <div>
                <h4 className="font-semibold text-gray-900">Required Documents</h4>
                <p className="text-gray-600">Photo ID, insurance card, and completed new patient forms</p>
              </div>
            </li>
            <li className="flex items-start">
              <Clock className="h-6 w-6 text-blue-600 mr-3 mt-1" />
              <div>
                <h4 className="font-semibold text-gray-900">Medical History</h4>
                <p className="text-gray-600">List of current medications and previous medical records</p>
              </div>
            </li>
            <li className="flex items-start">
              <MapPin className="h-6 w-6 text-blue-600 mr-3 mt-1" />
              <div>
                <h4 className="font-semibold text-gray-900">Referral Information</h4>
                <p className="text-gray-600">Any referral documentation from your primary care physician</p>
              </div>
            </li>
            <li className="flex items-start">
              <CreditCard className="h-6 w-6 text-blue-600 mr-3 mt-1" />
              <div>
                <h4 className="font-semibold text-gray-900">Payment Method</h4>
                <p className="text-gray-600">For any applicable co-pays or fees due at the visit</p>
              </div>
            </li>
          </ul>
        </div>
      </div>

      <div className="mb-16">
        <h2 className="text-2xl font-bold text-gray-900 mb-8">Required Information</h2>
        <div className="grid md:grid-cols-2 gap-8">
          <Link 
            to="/patient-info/insurance"
            className="bg-white rounded-lg p-6 shadow-md hover:shadow-lg transition-shadow"
          >
            <FileText className="h-8 w-8 text-blue-600 mb-3" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Insurance Information</h3>
            <p className="text-gray-600">Review accepted insurance plans and coverage details</p>
          </Link>
          <Link 
            to="/legal/hipaa-privacy-notice"
            className="bg-white rounded-lg p-6 shadow-md hover:shadow-lg transition-shadow"
          >
            <Shield className="h-8 w-8 text-blue-600 mb-3" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">HIPAA Privacy Notice</h3>
            <p className="text-gray-600">Our commitment to protecting your privacy</p>
          </Link>
        </div>
      </div>

      <div className="bg-gray-50 rounded-lg p-8 mb-16">
        <h2 className="text-2xl font-bold text-gray-900 mb-6">Office Location & Hours</h2>
        <div className="grid md:grid-cols-2 gap-8">
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Address</h3>
            <p className="text-gray-600 mb-4">
              1100 Northside Forsyth Dr., Suite 210<br />
              Cumming, GA 30041
            </p>
            <Link
              to="/contact"
              className="text-blue-600 hover:text-blue-700 font-medium"
            >
              Get Directions →
            </Link>
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Hours of Operation</h3>
            <p className="text-gray-600 mb-2">Monday–Thursday: 9:00 AM – 5:00 PM</p>
            <p className="text-gray-600">Friday: 9:00 AM – 12:00 PM</p>
          </div>
        </div>
      </div>

      <div className="text-center">
        <h2 className="text-2xl font-bold text-gray-900 mb-4">Ready to Schedule?</h2>
        <p className="text-gray-600 mb-8 max-w-2xl mx-auto">
          We look forward to providing you with exceptional neurological care. 
          Contact us today to schedule your first appointment.
        </p>
        <div className="flex justify-center gap-4">
          <Link
            to="/contact"
            className="bg-blue-600 text-white px-8 py-3 rounded-md font-semibold hover:bg-blue-700 transition-colors"
          >
            Schedule an Appointment
          </Link>
          <Link
            to="/patient-info/insurance"
            className="bg-white text-blue-600 border border-blue-600 px-8 py-3 rounded-md font-semibold hover:bg-blue-50 transition-colors"
          >
            Insurance Information
          </Link>
        </div>
      </div>
    </div>
  );
}